<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import SlideOver from '@/components/shared/SlideOver.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import VenueFormFields from '@/components/organizer/venues/VenueFormFields.vue'
import type { VenueFormData } from '@/components/organizer/venues/VenueFormFields.vue'
import { useVenues } from '@/composables/useVenues'
import type { Venue } from '@/types/events'

const props = defineProps<{
  open: boolean
  organisationId: number
  venueId: number | null
}>()

const emit = defineEmits<{
  close: []
  updated: [venue: Venue]
}>()

const { t } = useI18n()
const { fetchVenue, updateVenue, updateErrors, updating } = useVenues(props.organisationId)

const loading = ref(false)
const loadError = ref(false)
const loaded = ref<Venue | null>(null)
const form = ref<VenueFormData>({
  name: null,
  street: null,
  city: null,
  postal_code: null,
  country: null,
  timezone: '',
})

const canSave = computed<boolean>(
  () => loaded.value !== null && !loading.value && !updating.value && form.value.timezone !== '',
)

// Identifies the latest load() call, so only it may clear the loading flag.
let loadRequestId = 0

async function load(venueId: number): Promise<void> {
  const requestId = ++loadRequestId
  loading.value = true
  loadError.value = false
  loaded.value = null
  const venue = await fetchVenue(venueId)
  if (requestId !== loadRequestId) return
  loading.value = false
  // Ignore a late response for a venue the panel has since moved away from.
  if (props.venueId !== venueId || !props.open) return
  if (venue === null) {
    loadError.value = true
    return
  }
  loaded.value = venue
  form.value = {
    name: venue.name,
    street: venue.street,
    city: venue.city,
    postal_code: venue.postal_code,
    country: venue.country,
    timezone: venue.timezone,
  }
}

// Stale validation errors from a previous attempt must not reappear on reopen.
watch(
  () => props.open,
  () => {
    updateErrors.value = {}
  },
  { immediate: true },
)

watch(
  () => [props.open, props.venueId] as const,
  ([open, venueId]) => {
    if (open && venueId !== null) {
      void load(venueId)
    }
  },
  { immediate: true },
)

function handleClose(): void {
  if (updating.value) return
  emit('close')
}

async function handleSubmit(): Promise<void> {
  if (!canSave.value || props.venueId === null) return
  const venue = await updateVenue(props.venueId, {
    name: form.value.name,
    street: form.value.street,
    city: form.value.city,
    postal_code: form.value.postal_code,
    country: form.value.country,
    timezone: form.value.timezone,
  })
  if (venue !== null) {
    emit('updated', venue)
    emit('close')
  }
}
</script>

<template>
  <SlideOver :open="open" :title="t('venues.edit.title')" :saving="updating" @close="handleClose">
    <div v-if="loading" class="flex justify-center py-12" data-testid="venue-edit-loading">
      <AppSpinner />
    </div>
    <p
      v-else-if="loadError"
      class="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300"
      role="alert"
      data-testid="venue-edit-load-error"
    >
      {{ t('venues.edit.load_error') }}
    </p>
    <form v-else-if="loaded" id="venue-edit-form" novalidate @submit.prevent="handleSubmit">
      <VenueFormFields
        v-model="form"
        mode="edit"
        id-prefix="venue-edit"
        :errors="updateErrors"
        :initial-timezone="loaded.timezone"
        :usage="loaded.usage ?? null"
      />
    </form>

    <template #footer>
      <div class="flex items-center justify-end gap-3">
        <button
          type="button"
          :disabled="updating"
          class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="venue-edit-cancel"
          @click="handleClose"
        >
          {{ t('common.cancel') }}
        </button>
        <button
          type="button"
          :disabled="!canSave"
          class="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors flex items-center gap-2"
          data-testid="venue-edit-save"
          @click="handleSubmit"
        >
          <AppSpinner v-if="updating" size="sm" />
          {{ updating ? t('common.saving') : t('common.save') }}
        </button>
      </div>
    </template>
  </SlideOver>
</template>
